/**
 * GGD Warehouse Cross-Check v6
 * Always shows fulfillment warehouse line near ATC button.
 * Visible to all logged-in customers.
 * CA is default when both warehouses have stock.
 */

(function () {
  const LOCATIONS = {
    ca: { id: 'gid://shopify/Location/72416231655', label: 'California' },
    az: { id: 'gid://shopify/Location/86544679143', label: 'Arizona' },
  };

  // Cache per product handle
  let availabilityCache = {};

  function getSelectedVariantId() {
    const input =
      document.querySelector('product-info input[name="id"]') ||
      document.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  async function fetchAllVariantAvailability() {
    const token  = window.GGD_STOREFRONT_TOKEN;
    const shop   = window.Shopify && window.Shopify.shop;
    const handle = window.GGD_PRODUCT_HANDLE;
    if (!token || !shop || !handle) return null;

    if (availabilityCache[handle]) return availabilityCache[handle];

    const query = `
      query getStoreAvailability($handle: String!) {
        product(handle: $handle) {
          variants(first: 100) {
            edges {
              node {
                id
                storeAvailability(first: 10) {
                  edges {
                    node {
                      location { id }
                      available
                      quantityAvailable
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const res = await fetch(`https://${shop}/api/2024-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables: { handle } }),
      });

      const data = await res.json();
      const variants = data?.data?.product?.variants?.edges || [];

      const map = {};
      for (const { node } of variants) {
        map[node.id] = {};
        for (const { node: avail } of node.storeAvailability.edges) {
          map[node.id][avail.location.id] = {
            available: avail.available,
            quantity:  avail.quantityAvailable,
          };
        }
      }

      availabilityCache[handle] = map;
      return map;

    } catch (e) {
      console.warn('GGD cross-check: Storefront API error', e);
      return null;
    }
  }

  async function getStockAtLocation(variantId, locationKey) {
    const map = await fetchAllVariantAvailability();
    if (!map) return null;
    const variantGid = `gid://shopify/ProductVariant/${variantId}`;
    const variantData = map[variantGid];
    if (!variantData) return { available: false, quantity: 0 };
    const loc = variantData[LOCATIONS[locationKey].id];
    if (!loc) return { available: false, quantity: 0 };
    return loc;
  }

  // ── Determine fulfillment warehouse and update UI ──

  async function updateFulfillmentLine() {
    const wrapper       = document.getElementById('ggd-crosscheck');
    const fulfillEl     = document.getElementById('ggd-fulfillment-line');
    const iconEl        = document.getElementById('ggd-fulfillment-icon');
    const textEl        = document.getElementById('ggd-fulfillment-text');
    const atcBtn        = document.querySelector('.product-form__submit');

    if (!wrapper || !fulfillEl || !textEl) return;

    const variantId = getSelectedVariantId();
    if (!variantId) { wrapper.style.display = 'none'; return; }

    // Fetch both warehouses in parallel
    const [caStock, azStock] = await Promise.all([
      getStockAtLocation(variantId, 'ca'),
      getStockAtLocation(variantId, 'az'),
    ]);

    const caAvailable = caStock && caStock.available;
    const azAvailable = azStock && azStock.available;

    wrapper.style.display = 'block';
    fulfillEl.className = 'ggd-crosscheck__fulfillment';

    if (caAvailable) {
      // CA has stock — default to CA (even if AZ also has stock)
      iconEl.textContent = '🏭';
      textEl.textContent = 'This item will be shipped from the California Warehouse';
      restoreAtcButton(atcBtn);
      setCartFulfillmentNote(variantId, 'California');

    } else if (azAvailable) {
      // Only AZ has stock
      iconEl.textContent = '🏭';
      textEl.textContent = 'This item will be shipped from the Arizona Warehouse';
      restoreAtcButton(atcBtn);
      setCartFulfillmentNote(variantId, 'Arizona');

    } else {
      // Neither warehouse has stock
      fulfillEl.className = 'ggd-crosscheck__fulfillment ggd-crosscheck__fulfillment--unavailable';
      iconEl.textContent = '✕';
      textEl.textContent = 'Currently out of stock at all locations';
      disableAtcButton(atcBtn);
    }
  }

  // ── ATC button helpers ──

  function disableAtcButton(btn) {
    if (!btn) return;
    if (!btn.dataset.originalText) btn.dataset.originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Out of Stock';
  }

  function restoreAtcButton(btn) {
    if (!btn) return;
    btn.disabled = false;
    if (btn.dataset.originalText) {
      btn.textContent = btn.dataset.originalText;
    }
  }

  // ── Attach fulfillment note to cart add ──
  // Intercepts the form submit to inject the line item property

  let currentFulfillmentWarehouse = null;

  function setCartFulfillmentNote(variantId, warehouseLabel) {
    currentFulfillmentWarehouse = warehouseLabel;
  }

  function attachCartInterceptor() {
    const form = document.querySelector('product-info form[action="/cart/add"]') ||
                 document.querySelector('form[action="/cart/add"]');
    if (!form || form.dataset.ggdIntercepted) return;
    form.dataset.ggdIntercepted = 'true';

    form.addEventListener('submit', function (e) {
      if (!currentFulfillmentWarehouse) return;

      // Inject hidden input with fulfillment note if not already present
      let noteInput = form.querySelector('input[name="properties[_Fulfillment Note]"]');
      if (!noteInput) {
        noteInput = document.createElement('input');
        noteInput.type = 'hidden';
        noteInput.name = 'properties[_Fulfillment Note]';
        form.appendChild(noteInput);
      }
      noteInput.value = `These items will be shipped from the ${currentFulfillmentWarehouse} warehouse`;
    }, { passive: true });
  }

  // ── Init ──

  function init() {
    const wrapper = document.getElementById('ggd-crosscheck');
    if (!wrapper) return;

    // Position the crosscheck div right after the ATC form
    const atcBtn = document.querySelector('.product-form__submit');
    if (atcBtn) {
      const form = atcBtn.closest('form');
      if (form) {
        form.parentNode.insertAdjacentElement('afterend', wrapper);
      } else {
        atcBtn.parentNode.insertAdjacentElement('afterend', wrapper);
      }
    }

    // Pre-fetch on load for snappy response
    fetchAllVariantAvailability();
    updateFulfillmentLine();
    attachCartInterceptor();

    // Update on variant change
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') {
        setTimeout(updateFulfillmentLine, 100);
      }
    });
    document.addEventListener('variant:change', function () {
      setTimeout(updateFulfillmentLine, 100);
    });
    if (typeof window.subscribe === 'function') {
      window.subscribe('variant:change', function () {
        setTimeout(updateFulfillmentLine, 100);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();